<template>
  <div class="query-builder">
    <side-panel
      v-if="showQueryList"
      :onClose="() => showQueryList = false"
      background="gray"
      class="article-queries-side-panel"
    >
      <AppArticleQueries
        :show-add-query="false"
        :inside-sidepanel="true"
        :allow-delete-folders="false"
        :small="true"
      />
    </side-panel>
    
    <div class="qb-top-header flex-layout">
      <div>
        <div class="project-name text-orange">
          <span class="text-xl link-pointer" v-on:click="redirectToQueryList">
          <span class="far fa-long-arrow-left text-lg icon-orange pr10" style="line-height: 32px" aria-hidden="true"></span>
          {{settings && settings.data && settings.data.project_name}}
        </span>
        </div>

        <div class="icon-btn-input active-show-icon ml20" style="width: calc(50% - 30px); display: inline-block;">
          <input 
            v-model.trim="query.name"
            :placeholder="$t('untitled_query')"
            :class="{ error : errors.name }"
            class="input-ui input-ui-minimal text-xl nmb"
            type="text"
          >
          <span class="fas fa-pencil"></span>
        </div>
      </div>
      <div class="qb-top-header-buttons">

        <span
          v-tooltip="$t('saved_queries')"
          class="btn btn-default btn-icon btn-saved-queries"
          @click="showQueryList = true"
        >
          <span class="far fa-sign-out-alt"></span>
        </span>

        <span class="btn btn-default btn-reset"
              @click="loadQuery">
          {{$t('actions.reset')}}
        </span>

        <span class="btn btn-carma btn-save">
          <span 
            class="qb-save" 
            @click="(e) => onSaveQuery(e, SAVE_ACTIONS.SAVE)"
          >
            {{$t('actions.save')}}
          </span>
          <app-dropdown-button
              :options="saveOptions"
              customCss="qb-save-dropdown"
              @saveAs="(e) => onSaveQuery(e, SAVE_ACTIONS.SAVE_AS)"
              @edit="(e) => onSaveQuery(e, SAVE_ACTIONS.EDIT)"
              @createNew="newQuery"
          />
        </span>
      </div>
    </div>

    <div class="qb-main flex-layout">
      <div class="qb-sidebar p10"> 
        <app-category-tags-panel
          :query="query"
          :onDrag="onDragFilters"
          :isInsight="isInsight"
          @setCategoriesHiddenForInsight="(e) => setCategoriesHiddenForInsight(e)"
        />
      </div>
      <div class="qb-workspace flex-3 flex-layout">
        <app-results-panel v-if="settings && settings.data && settings.data.outputs"
                           class="flex-5"
                           :showArticleList="showArticleList"
                           :query="query"
                           :queryResult="queryResult"
                           :dragging="draggingFilters && draggingFilters.indexOf(ALLOW_DRAG.AXIS) !== -1"
                           :onDrop="onDropFilters"
                           :required="errors.filters"
                           :blocked="blocked"
                           :localTextOptions="localTextOptions"
                           :localHeadlineOptions="localHeadlineOptions"
                           :articlesTablePagination="articlesTablePagination"
                           :queryResultPagination="queryResultPagination"
                           @setQueryEntity="setQueryEntity"
                           @removeElement="removeElement"
                           @export="onExportQuery"
                           @run="runQueryRequest"
                           @switchListView="switchListView"
        />

        <app-filters-panel class="flex-2"
          :onDrop="onDropFilters"
          :filters="query.filters"
          :querySettings="query.query_settings"
          :dragging="draggingFilters && draggingFilters.indexOf(ALLOW_DRAG.FILTERS) !== -1"
          @setQueryEntity="setQueryEntity"
        />
      </div>

      <div class="qb-sidebar p10">
        <app-breakdown-filters-panel
          :query="query"
          :outputs="settings && settings.data && settings.data.outputs || []"
          :filters="query.filters"
          :onDrag="onDragFilters"
          :dragging="draggingFilters"
          :localTextOptions="localTextOptions"
          :localHeadlineOptions="localHeadlineOptions"
          @setQueryEntity="setQueryEntity"
          @setLocalTextOptions="setLocalTextOptions"
        />
      </div>
    </div>

    <app-save-query-modal
      v-if="showSave"
      :query="query"
      :isEdit="showSave === SAVE_ACTIONS.EDIT"
      @cancel="onCancelSave"
      @save="onSubmitSave"
      @setQueryEntity="setQueryEntity"
    />

    <query-export-modal v-if="exportQueryBusy" :exportObj="exportObj"/>

    <app-run-error-modal
      v-if="errorMessage"
      :title="$t('errors.unable_run_query')"
      :text="$t('errors.' + errorMessage)"
      @closeModal="closeModal"
    />
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import _ from 'lodash';
  import moment from 'moment';

  import CategoryTagsPanel from '../components/coding-queries/category_tags_panel';
  import ResultsPanel from '../components/coding-queries/results_panel';
  import FiltersPanel from '../components/coding-queries/filters_panel';
  import BreakdownFiltersPanel from '../components/coding-queries/breakdown_filters_panel';
  import SaveQueryModal from '../components/coding-queries/save_query_modal';
  import QueryExportModal from '../components/coding-queries/QueryExportModal.vue';
  import DropdownButton from '../components/dropdown-button';
  import SidePanel from '../components/side-panel';
  import AppArticleQueries from '../components/coding-admin/article-queries';
  import RunErrorModal from '../components/coding-queries/run_error_modal';


  import array from '../helpers/array';
  import SHORTCUT from '../config/query-shortcut-keys';
  import { QUERIES, ARTICLE_STATUS } from '../config/const';
  import dateHelper from "@/helpers/date";

  export default {
    components: {
      'app-category-tags-panel': CategoryTagsPanel,
      'app-results-panel': ResultsPanel,
      'app-filters-panel': FiltersPanel,
      'app-breakdown-filters-panel': BreakdownFiltersPanel,
      'app-save-query-modal': SaveQueryModal,
      'app-dropdown-button': DropdownButton,
      SidePanel,
      AppArticleQueries,
      QueryExportModal,
      'app-run-error-modal': RunErrorModal
    },
    computed: {
      ...mapState({
        isInsight: state => state.isInsight,
        insightSessionToken: state => state.insightSessionToken,
        queryData: state => state.queryBuilder.query.query,
        settings: state => state.queryBuilder.settings.settings,
        exportObj: state => state.queryBuilder.query.export,
        exportQueryBusy: state => state.queryBuilder.query.export.isBusy,
        queryResult: state => state.queryBuilder.query.run,
        queryResultPagination: state => !_.isEmpty(_.get(state.queryBuilder.query.run, 'pagination')) ? state.queryBuilder.query.run.pagination : {},
        articlesPagination: state => !_.isEmpty(_.get(state.queryBuilder.articles.articles, 'data.pagination')) ? state.queryBuilder.articles.articles.data.pagination : {}
      }),
      queryId() {
        return this.$route.params.query_id;
      },
      projectId() {
        return this.$route.params.project_id;
      },
      saveOptions() {
        let options = [];

        if(this.queryId) {
          options.push({label: 'Edit', action: QUERIES.SAVE_ACTIONS.EDIT});
          options.push({label: 'Save As', action: QUERIES.SAVE_ACTIONS.SAVE_AS});
          options.push({label: 'Create New ', action: QUERIES.SAVE_ACTIONS.CREATE_NEW});
        }

        return options;
      }
    },
    created() {
      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('keyup', this.handleKeyUp);

      if(!this.settings.isBusy) {
        this.getSettings({
          projectId: this.projectId
        });
      }

      this.loadQuery();
      this.$emit('setQueryEntity', { entity: 'query_settings', value: this.query.query_settings});

      if(Object.keys(this.articlesPagination).length > 0) {
        this.articlesTablePagination = _.cloneDeep(this.articlesPagination);
      }
    },
    beforeDestroy() {
      window.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('keyup', this.handleKeyUp);
    },
    data: function() {
      const date = moment(new Date()).format("YYYY-MM-DD");

      return {
        showQueryList: false,
        query: {},
        draggingFilters: false,
        ALLOW_DRAG: QUERIES.ALLOW_DRAG,
        showSave: false,
        currentKeys: [],
        blocked: {
          rows: false,
          columns: false
        },
        errors: {
          filters: {}
        },
        localTextOptions: [],
        localHeadlineOptions: [],
        showArticleList: false,
        cellClicked: false,
        articlesTablePagination: {
          perPage: 25,
          currentPage: 1,
          order: 'date',
          sort: 'desc'
        },
        queryParamsChanged: false,
        SAVE_ACTIONS: QUERIES.SAVE_ACTIONS,
        errorMessage: null,
        hasCategoriesHiddenForInsight: false
      }
    },
    watch: {
      'queryData.data': {
        handler(value) {

          this.showSave = false;
          if (value.id && parseInt(this.queryId) !== parseInt(value.id)) {
            let url = `/analysis/queries/${this.projectId}/${value.id}`
            if(this.isInsight) url += '?insight=true'
            if(this.insightSessionToken) url += `&session_token=${this.insightSessionToken}`
            window.location.href = url;
          }
          else {
            this.query = {
              ...this.query,
              ...value
            };

            if (!this.query.article_status.length) {
              this.query.article_status = [ARTICLE_STATUS.CODED, ARTICLE_STATUS.PUBLISHED_QC, ARTICLE_STATUS.PUBLISHED_NOT_QC];
            }

            this.runQueryRequest({ query: this.query })
          }
        }
      },

      'query.default_output': {
        handler (value) {
          if(value !== _.get(this.query, 'order.output_id')) {
            this.$delete(this.query, 'sort')
            this.$delete(this.query, 'order')
          }

          if(this.query.rows && this.query.rows.length > 0) {
            this.runQueryRequest(this.query);
          }
        }
      },

      'articlesPagination': {
          handler(newValue) {
            if(Object.keys(newValue).length > 0 && !_.isEqual(newValue, this.articlesTablePagination)) {
              this.articlesTablePagination = _.cloneDeep(newValue);
            }
          }
        },

      'queryResult.error': {
        handler (data) {
          if (data) {
            const error = data.response.data.message;
            if ([QUERIES.ERRORS.INVALID_MV_REQUIREMENTS, QUERIES.ERRORS.MV_NOT_FOUND].indexOf(error) > -1) {
              this.errorMessage = error;
            }
          }
        }
      },

      'query.query_settings.filter_at_article_level': {
        handler(data) {
          this.query = { ...this.query, query_settings: {filter_at_article_level: data} };
          this.$emit('setQueryEntity', { entity: 'query_settings', value:  { filter_at_article_level: this.query.query_settings.filter_at_article_level } });
          this.runQueryRequest({ query: this.query });
        }
      },
    },
    methods: {
      ...mapActions({
        'getQuery': 'queryBuilder/query/getQuery',
        'getSettings': 'queryBuilder/settings/getSettings',
        'exportQuery': 'queryBuilder/query/exportQuery',
        'runQuery': 'queryBuilder/query/runQuery',
        'saveQuery': 'queryBuilder/query/saveQuery',
        'updateQuery': 'queryBuilder/query/updateQuery',
        'getArticles': 'queryBuilder/articles/getArticles'
      }),

      setEmptyQuery() {
        
        this.query = {
          columns: [],
          rows: [],
          period: {
            type: QUERIES.TIME_PERIOD_TYPE.CURRENT,
            range: dateHelper.getQueryDefaultDateRange()
          },
          default_output: 1,
          outputs: [1],
          output_settings: [],
          external_filters: [],
          filters: [],
          query_settings: {
            filter_at_article_level: false
          },
          limit: 10,
          name: 'Untitled Query',
          target: [],
          is_private: true,
          folder_id: null,
          custom_breakdown: [],
          article_status: [ARTICLE_STATUS.CODED, ARTICLE_STATUS.PUBLISHED_QC, ARTICLE_STATUS.PUBLISHED_NOT_QC]
        }
      },

      loadQuery() {
        const { projectId, queryId } = this;

        this.setEmptyQuery();

        if(queryId)
          this.getQuery({ projectId, queryId });
      },

      newQuery() {
        this.$router.push({path: `/analysis/queries/${this.projectId}`});
        this.$router.go(0);
      },

      runQueryRequest ({ query, pagination } = {}) {
        if(!query || (query && query.srcElement))
          query = this.query;

        const { projectId } = this;
        if (this.queryParamsChanged) {
          this.articlesTablePagination.currentPage = 1;
          this.queryParamsChanged = false;
        }

        if (this.showArticleList) {
          if(pagination) {
            this.articlesTablePagination = { ...this.articlesTablePagination, ...pagination };
          }

          let queryValue = _.cloneDeep(query);
          if (this.cellClicked) {
            queryValue.columns = [this.cellClicked.column];
            queryValue.rows = [this.cellClicked.row];
          }

          // if its a custom_compare_date_range clicked col, 
          // swap date ranges for BE to resovle with correct col date range, as its looking at the period->range val 
          if(this.cellClicked?.column?.range?.type === "custom_compare_date_range" && queryValue?.period?.custom_compare_date_range) {
            queryValue.period.range = queryValue?.period?.custom_compare_date_range;
          }

          this.getArticles({
            projectId,
            filterValues: queryValue,
            pagination: this.articlesTablePagination
          });
        }
        else {
          //validate
          if(!this.validateQuery(query))
            return;

          //Run only if there is something on the rows
          if(query.rows && query.rows.length > 0 && !this.queryResult.isBusy) {
            this.runQuery({ projectId, query });
          }
        }
      },

      switchListView ({ showArticleList, cellClicked } = {}) {
        // if its trend, do nothing
        if(cellClicked?.column?.type === 'trend' || cellClicked?.column?.original_col.column === 'rank') return;

        if(!_.isUndefined(showArticleList)) {
          showArticleList = !this.showArticleList;
        }

        this.showArticleList = showArticleList;
        if (!_.isEqual(this.cellClicked, cellClicked)) {
          this.queryParamsChanged = true;
        }
        this.cellClicked = cellClicked;
        this.runQueryRequest();
      },

      onDragFilters(dragging, dragItem) {
        //Dragging can be filtersOnly, axisOnly, all
        this.draggingFilters = dragging;

        if (!dragging) {
          this.blocked = {
            columns: false,
            rows: false
          }
        }
        else if(dragging.indexOf(QUERIES.ALLOW_DRAG.AXIS) !== -1) {
          const row = this.query.rows && this.query.rows[0];
          const column = this.query.columns && this.query.columns[0];
          let columnsBlocked, rowsBlocked = false, isTrendPeriod = typeof dragItem?.[0]?.name === 'string' && dragItem?.[0]?.name?.toLocaleLowerCase()?.match(/trend[_|\s]periods/i);
          this.errors.filters = {};

          switch(dragItem[0].type) {
            case 'tag':
              if (row && row.type === 'tag') {
                columnsBlocked = row.category_id === dragItem[0].category_id;
              }
              else if (row && row.type === 'category') {
                columnsBlocked = row.id === dragItem[0].category_id;
              }

              if (column && column.type === 'tag') {
                rowsBlocked = column.category_id === dragItem[0].category_id;
              }
              else if (column && column.type === 'category') {
                rowsBlocked = column.id === dragItem[0].category_id;
              }
              break;

            case 'category':
              if (row && row.type === 'tag') {
                columnsBlocked = row.category_id === dragItem[0].id;
              }
              else if (row && row.type === 'category') {
                columnsBlocked = row.id === dragItem[0].id;
              }

              if (column && column.type === 'tag') {
                rowsBlocked = column.category_id === dragItem[0].id;
              }
              else if (column && column.type === 'category') {
                rowsBlocked = column.id === dragItem[0].id;
              }
              break;

            case 'breakdown':
              if(row && row.type === 'breakdown' || isTrendPeriod) {
                columnsBlocked = (row.id === dragItem[0].id && row.parent_id === dragItem[0].parent_id) || (row.parent_id && dragItem[0].parent_id && row.parent_id === dragItem[0].parent_id) || (row.id === dragItem[0].parent_id) || (row.parent_id === dragItem[0].id) || isTrendPeriod;
              }

              if(column && column.type === 'breakdown') {
                rowsBlocked = (column.id === dragItem[0].id && column.parent_id === dragItem[0].parent_id) || (column.parent_id && dragItem[0].parent_id && column.parent_id === dragItem[0].parent_id) || (column.id === dragItem[0].parent_id) || (column.parent_id === dragItem[0].id);
              }
              break;
          }

          this.blocked = {
            columns: columnsBlocked,
            rows: rowsBlocked
          }
        }
      },

      onDropFilters() {
        this.draggingFilters = false;
      },

      removeElement({entity, item}) {
        if (item.values && item.values.length && item.values[0].tag_id)
          item.tag_id = item.values[0].tag_id;

        this.query[entity] = this.query[entity].filter(value => {
          return value.tag_id !== item.tag_id || value.category_id !== item.category_id;
        });

        this.runQueryRequest({ query: this.query })
      },

      setQueryEntity ({entity, value, noRequest}) {
        if(!this.query.hasOwnProperty(entity)) return this.$set(this.query, entity, value);

        // if only needed to update the query obj value
        if(noRequest) return this.query[entity] = value;

        if (
          value &&
          value.length &&
          this.isTagsItem(value[0]) &&
          entity !== QUERIES.ALLOW_DRAG.FILTERS &&
          this.query[entity].length && this.isTagsItem(this.query[entity][0])
        ) {
          const newItems = [];

          value.forEach(element => {
            const hasSameItem = this.query[entity].find(item => {
              return item.tag_id === element.tag_id && item.category_id === element.category_id;
            });

            if (!hasSameItem) { 
              newItems.push(element);
            }
          });

          this.query[entity] = [...this.query[entity], ...newItems];
        } else {
          this.query[entity] = value;
        }

        this.updateQuery(_.cloneDeep(this.query));
      },

      isTagsItem(item) {
        return item && item.hasOwnProperty('tag_id');
      },

      onSaveQuery (e, action) {
        if(e) {
          e.preventDefault();
          e.stopPropagation();
        }

        //validate
        if(!this.validateQuery(this.query))
          return;

        if(!this.queryId || (action && [QUERIES.SAVE_ACTIONS.EDIT, QUERIES.SAVE_ACTIONS.SAVE_AS].indexOf(action) !== -1)) {
          this.showSave = action;
        }
        else {
          this.onSubmitSave({ projectId: this.queryData?.data?.project_id || this.projectId, query: this.query})
        }
      },

      onCancelSave () {
        this.showSave = false;
      },

      onSubmitSave ({ projectId, query }) {
        this.saveQuery({ projectId, query });
      },

      validateQuery(query){
        let formValid = true;
        //const isEmptyColumns = _.isEmpty(query.columns);
        const isEmptyRows = _.isEmpty(query.rows);

        if(isEmptyRows) {
          formValid = false;
          this.errors.filters = {
            rows: isEmptyRows
          }
        } else if(this.errors.filters){
          this.errors.filters = {};
        } 
        
        if (!query.name) {
          formValid = false;
          this.errors.name = true;
        } else {
          this.errors.name = false;
        }

        return formValid;
      },

      redirectToQueryList (e) {
        if(e) {
          e.preventDefault();
          e.stopPropagation();
        }

        let url = `/analysis/projects/${this.projectId}/queries`
        if(this.isInsight) url += '?insight=true'
        if(this.insightSessionToken) url += `&session_token=${this.insightSessionToken}`
        window.location.href = url;
      },

      handleKeyDown (e) {
        var node = (e.target) ? e.target : ((e.srcElement) ? e.srcElement : null);
        if(node.closest('.form-control'))
          return;

        this.currentKeys.push({ key: e.key.toLowerCase(), keyCode: e.keyCode || e.which });
      },

      handleKeyUp (e) {
        var node = (e.target) ? e.target : ((e.srcElement) ? e.srcElement : null);
        if(node.closest('.form-control'))
          return;

        const pressedKey = e.keyCode || e.which;
        let command;
        const commandKeyCodes = [16, 17, 18, 91, 93];

        //if key is a command key and it was the only one recorded on keydown, get relevant command
        if(commandKeyCodes.indexOf(pressedKey) !== -1) {
          if(this.currentKeys.length <= 1) {
            command = Object.keys(SHORTCUT.shortcuts).filter(shortcut => {
              return _.isEqual(SHORTCUT.shortcuts[shortcut], [{ key: e.key.toLowerCase(), keyCode: pressedKey }]);
            });

            //clear currentKeys
            this.clearCurrentKeys();
          }
        }
        //if not a command key
        else {
          //check if any on the command key pressed is true
          const shortcutKeysPressed = [{ key: e.key.toLowerCase(), keyCode: pressedKey }];
          if(e.ctrlKey)
            shortcutKeysPressed.push({ key: 'control', keyCode: 17 });
          if(e.shiftKey)
            shortcutKeysPressed.push({ key: 'shift', keyCode: 16 });
          if(e.altKey)
            shortcutKeysPressed.push({ key: 'alt', keyCode: 18 });
          if(e.metaKey)
            shortcutKeysPressed.push({ key: 'meta'});

          const sortedCurrentKeys = array.sortArrayByString(shortcutKeysPressed, 'key');
          //find command with those keys and execute
          command = Object.keys(SHORTCUT.shortcuts).filter(shortcut => {
            const sortedShortcut = array.sortArrayByString(SHORTCUT.shortcuts[shortcut], 'key');
            return _.isEqual(sortedCurrentKeys, sortedShortcut);
          });

          this.clearCurrentKeys();
        }

        if(command) {
          //Execute command if not empty
          this.executeShortcutCommands(command);
        }
      },

      clearCurrentKeys() {
        if (this.currentKeys.length > 0) {
          this.currentKeys = [];
        }
      },

      executeShortcutCommands(commands) {
        const elementFocus = document.activeElement.tagName.toLowerCase();

        if (elementFocus === 'input') return;

        commands.map(command => {
          switch (command) {
            case QUERIES.SHORTCUT_COMMANDS.RUN:
              this.runQueryRequest();
              break;
            case QUERIES.SHORTCUT_COMMANDS.SAVE:
              this.onSaveQuery();
              break;
            case QUERIES.SHORTCUT_COMMANDS.BACK_TO_QUERY_LIST:
              this.redirectToQueryList();
              break;
          }
        });
      },

      getOutputById (id) {
        return this.settings.data.outputs.find(output => output.output_id === id);
      },

      setLocalTextOptions (newValue, type) {
        if (type == 'headline') {
          this.localHeadlineOptions = newValue;
        }
        else {
          this.localTextOptions = newValue;
        }
      },

      onExportQuery ({ formatType, level, include_fulltext }) {
        let query = _.cloneDeep(this.query);
        //when doing an export of a result table we do not want to limit the number of records
        query.limit = 10000;

        if (this.cellClicked) {
          query.columns = [this.cellClicked.column];
          query.rows = [this.cellClicked.row];
        }

        this.exportQuery({ projectId: this.projectId, query, formatType, level, include_fulltext: !!include_fulltext })
      },

      setCategoriesHiddenForInsight( hasHidden = false){
        this.hasCategoriesHiddenForInsight = hasHidden
      },

      closeModal () {
        this.errorMessage = null;
      }
    }
  }
</script>

<style lang="scss">
  @import "../styles/components/coding-queries/layout.scss";

.query-builder {
  .span-add-row {
    display: none;
  }
}
</style>